/**
 * The auth endpoints this slice uses, one function each.
 *
 * Every request body and every response is described by a contract from
 * `@wend/shared` — nothing is re-declared here. If a shape needs to change it
 * changes in the contract, and both this app and the API stop compiling.
 */
import {
  loginRequestSchema,
  loginResponseSchema,
  logoutResponseSchema,
  meResponseSchema,
  okResponseSchema,
  setPasswordResponseSchema,
  verifySetPasswordTokenResponseSchema,
  type LoginRequest,
  type LoginResponse,
  type SessionUser,
  type SetPasswordRequest,
  type SetPasswordResponse,
  type VerifySetPasswordTokenResponse,
} from '@wend/shared';

import { adoptSession, apiRequest, clearSession } from './client';

/**
 * Email + password. There is no workspace field: `users.email` is globally
 * unique, so the address alone resolves the tenant.
 *
 * The refresh token comes back as an httpOnly cookie, not in this response.
 */
export async function login(credentials: LoginRequest): Promise<LoginResponse> {
  const session = await apiRequest({
    path: '/auth/login',
    method: 'POST',
    // Normalises the email (trim + lower-case) exactly as the API will.
    body: loginRequestSchema.parse(credentials),
    schema: loginResponseSchema,
    auth: false,
  });

  adoptSession(session);
  return session;
}

/**
 * Revokes the refresh token and clears its cookie. Local state is dropped even
 * if the call fails — a user who pressed "sign out" is signed out of this
 * browser regardless of what the network did.
 */
export async function logout(): Promise<void> {
  try {
    await apiRequest({
      path: '/auth/logout',
      method: 'POST',
      schema: logoutResponseSchema,
      auth: false,
    });
  } finally {
    clearSession();
  }
}

/** The authenticated shell's identity check. Goes through the RLS-bound path. */
export async function fetchCurrentUser(signal?: AbortSignal): Promise<SessionUser> {
  const response = await apiRequest({
    path: '/auth/me',
    schema: meResponseSchema,
    signal,
  });
  return response.user;
}

/**
 * Pre-flight for the set-password screen. An expired or already-used link is an
 * expected answer (`{ valid: false }`), not an error.
 */
export async function verifySetPasswordToken(
  token: string,
  signal?: AbortSignal,
): Promise<VerifySetPasswordTokenResponse> {
  return apiRequest({
    path: '/auth/set-password',
    query: { token },
    schema: verifySetPasswordTokenResponseSchema,
    auth: false,
    signal,
  });
}

/**
 * Redeems the token and stores the chosen password.
 *
 * Deliberately returns no session — the API's contract is `{ success, email }`.
 * The screen signs the user in afterwards with the password they just chose.
 */
export async function completeSetPassword(
  input: SetPasswordRequest,
): Promise<SetPasswordResponse> {
  return apiRequest({
    path: '/auth/set-password',
    method: 'POST',
    body: input,
    schema: setPasswordResponseSchema,
    auth: false,
  });
}

/**
 * Asks for a fresh onboarding link. Always resolves, whether or not the address
 * exists — the API answers the same either way so this endpoint cannot be used
 * as a customer directory, and the UI must not imply otherwise.
 */
export async function requestSetPasswordLink(email: string): Promise<void> {
  await apiRequest({
    path: '/auth/set-password/request',
    method: 'POST',
    body: { email },
    schema: okResponseSchema,
    auth: false,
  });
}
